// 주문 도메인 공통 타입과 zod 스키마.
// 사용자 앱(주문 생성/조회), 관리자 콘솔(주문 관리), API 가 모두 본 파일을 공유한다.
// 주문 상태 enum/전환표는 orderStatus.ts 에 분리돼 있으므로 본 파일은 "유형/입력/응답 DTO" 만 다룬다.

import { z } from "zod";
import { ORDER_STATUS, type OrderStatus } from "./orderStatus.js";
import { calendarDateSchema, timeOfDaySchema } from "./operations.js";
import { type ProductUnit } from "./units.js";
import { type CouponType } from "./coupon.js";
import {
  PAYMENT_METHOD_TYPE,
  allowedPaymentMethodTypes,
  type PaymentMethodType,
  type PaymentStatus,
} from "./payment.js";

// 주문 유형. 작업계획서 4.5 의 "포장 주문 / 배달 주문".
export const ORDER_TYPE = {
  PICKUP: "pickup",
  DELIVERY: "delivery",
} as const;
export type OrderType = (typeof ORDER_TYPE)[keyof typeof ORDER_TYPE];

export const ORDER_TYPE_LABEL_KO: Record<OrderType, string> = {
  pickup: "포장",
  delivery: "배달",
};

// 취소 주체. status = 'canceled' 일 때만 의미가 있다.
export const ORDER_CANCELED_BY = {
  USER: "user",
  ADMIN: "admin",
} as const;
export type OrderCanceledBy = (typeof ORDER_CANCELED_BY)[keyof typeof ORDER_CANCELED_BY];

// 손님 요청사항 최대 길이. 과도한 입력을 막아 UI/저장 사고를 차단한다.
export const ORDER_MEMO_MAX = 500;
// 거절 사유 최대 길이.
export const ORDER_REJECT_REASON_MAX = 300;

// 주문 생성 요청.
//  - 주문 항목은 본 입력에 담지 않는다. 서버는 호출자의 "서버 카트" 를 신뢰해 항목을 구성한다 (클라이언트 가격/수량 신뢰 금지).
//  - desiredDate/desiredTime 은 예약 슬롯. 둘 다 비우면 "지금 가능한 시간(ASAP)" 으로 간주한다.
//  - addressId 는 배달 주문일 때만 필수다. 본인 소유 주소인지/배달 가능 지역인지는 서버에서 재검증한다.
export const createOrderInputSchema = z
  .object({
    type: z.union([z.literal(ORDER_TYPE.PICKUP), z.literal(ORDER_TYPE.DELIVERY)]),
    desiredDate: calendarDateSchema.nullable().optional(),
    desiredTime: timeOfDaySchema.nullable().optional(),
    addressId: z.string().min(1).nullable().optional(),
    customerMemo: z.string().trim().max(ORDER_MEMO_MAX).nullable().optional(),
    // 적용 쿠폰 (선택). 할인 쿠폰 1장 + 배달비무료 쿠폰 1장까지. 서버가 소유/유효/종류를 재검증한다.
    discountCouponId: z.string().min(1).nullable().optional(),
    deliveryCouponId: z.string().min(1).nullable().optional(),
    // 사용할 적립금(원, 1P=1원). 서버가 잔액/결제금액 한도로 보정한다.
    pointsUsed: z.number().int().min(0).optional(),
    // 결제수단. 미지정이면 서버가 기존 동작(현장/onsite)으로 처리한다(비파괴).
    //  - 유형별 허용: 포장=[inapp_direct, onsite] / 배달=[inapp_direct, sms_request].
    //  - inapp(빌링키)은 폐지돼 서버 라우트가 거절한다. 실중량 대상품 포함 여부에 따른 제한도 라우트가 판정한다
    //    (스키마는 카트 내용을 모르므로 여기서는 "주문 유형에 구조적으로 가능한 수단"만 본다).
    paymentMethodType: z
      .union([
        z.literal(PAYMENT_METHOD_TYPE.ONSITE),
        z.literal(PAYMENT_METHOD_TYPE.INAPP),
        z.literal(PAYMENT_METHOD_TYPE.INAPP_DIRECT),
        z.literal(PAYMENT_METHOD_TYPE.SMS_REQUEST),
      ])
      .optional(),
    // 인앱(카드) 결제 시 사용할 본인 빌링키 결제수단 id. 서버가 소유/유효성을 재검증한다.
    billingPaymentMethodId: z.string().min(1).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    // 배달 주문은 주소가 반드시 필요하다.
    if (v.type === ORDER_TYPE.DELIVERY && !v.addressId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["addressId"],
        message: "배달 주문은 배송지를 선택해야 합니다.",
      });
    }
    // 예약 슬롯은 날짜/시간을 함께 채우거나 함께 비워야 한다 (혼동 방지).
    if ((!!v.desiredDate) !== (!!v.desiredTime)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["desiredTime"],
        message: "희망 수령 시간은 날짜와 시간을 함께 선택해야 합니다.",
      });
    }
    // 결제수단을 명시했다면 주문 유형이 허용하는 수단인지 교차검증한다(미지정이면 현장 경로로 처리).
    //  - 실중량 대상품 포함 여부는 카트를 봐야 알 수 있어 서버 라우트가 최종 판정한다.
    //  - 폐지된 빌링키(inapp)는 아래에서 따로 막는다.
    if (
      v.paymentMethodType &&
      v.paymentMethodType !== PAYMENT_METHOD_TYPE.INAPP &&
      !allowedPaymentMethodTypes(v.type).includes(v.paymentMethodType)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["paymentMethodType"],
        message: "선택한 결제수단을 사용할 수 없는 주문 방식입니다.",
      });
    }
    // 등록 카드(빌링키) 결제는 폐지됐다(KPN 정기결제 MID 미발급).
    if (v.paymentMethodType === PAYMENT_METHOD_TYPE.INAPP) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["paymentMethodType"],
        message: "등록 카드 결제는 더 이상 사용할 수 없습니다.",
      });
    }
  });
export type CreateOrderInput = z.infer<typeof createOrderInputSchema>;

// 관리자: 주문 상태 변경 요청.
//  - 허용 전환 여부는 서버(orderStatus.canTransitionOrderStatus)가 최종 판정한다.
//  - status = 'rejected' 인 경우 거절 사유(rejectReason)를 필수로 받는다 (작업계획서 보완: 거절 사유 입력).
// 관리자가 설정할 수 있는 상태는 accepted/rejected/ready/completed + canceled 다.
//  - canceled: 진행 도중 관리자 취소(개선). 손님 직접 취소(수락 전)와 enum 은 같지만 경로/제한이 다르다.
//  - preparing 은 흐름에서 제거됐다 (작업지시 4).
// 실중량 입력 1건(준비완료 시). 항목 id 와 실제 중량(g).
export const orderItemWeightInputSchema = z.object({
  itemId: z.string().min(1),
  actualWeightG: z.number().int().positive().max(1_000_000),
});
export type OrderItemWeightInput = z.infer<typeof orderItemWeightInputSchema>;

export const adminUpdateOrderStatusInputSchema = z
  .object({
    status: z.union([
      z.literal(ORDER_STATUS.ACCEPTED),
      z.literal(ORDER_STATUS.REJECTED),
      z.literal(ORDER_STATUS.READY),
      z.literal(ORDER_STATUS.COMPLETED),
      z.literal(ORDER_STATUS.CANCELED),
    ]),
    rejectReason: z.string().trim().max(ORDER_REJECT_REASON_MAX).nullable().optional(),
    // 준비완료(ready) 전환 시 무게가변 항목의 실중량(g). 서버가 이 값으로 최종 금액을 재계산한다.
    //  - 다른 전환에는 무시된다. 일부 항목만 보낼 수 있다(미입력 항목은 예상치 유지).
    weights: z.array(orderItemWeightInputSchema).max(100).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.status === ORDER_STATUS.REJECTED && (!v.rejectReason || v.rejectReason.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["rejectReason"],
        message: "거절 사유를 입력해야 합니다.",
      });
    }
  });
export type AdminUpdateOrderStatusInput = z.infer<typeof adminUpdateOrderStatusInputSchema>;

// 응답: 주문 항목에 선택된 추가 옵션 (스냅샷).
// 주문 시점의 문구/중량/금액을 그대로 보관하므로 이후 상품 옵션이 바뀌거나 삭제돼도 내역은 불변이다.
export interface OrderItemOptionDto {
  label: string;
  addGrams: number;
  addPriceKrw: number;
  quantity: number;
}

// 응답: 주문 항목 (스냅샷).
// 주문 시점에 고정된 값이므로 products 의 현재 상태와 무관하다.
export interface OrderItemDto {
  id: string;
  productId: string;
  name: string;
  unit: ProductUnit;
  unitPriceKrw: number;
  imageKey: string | null;
  quantity: number;
  // 선택된 추가 옵션(없으면 빈 배열). 금액/중량은 아래 lineTotalKrw 와 예상 중량에 이미 합산되어 있다.
  options: OrderItemOptionDto[];
  // 라인 합계. 실중량 확정 전엔 예상치(단가*수량 + 옵션합), 확정 후엔 실중량 기준 최종치.
  lineTotalKrw: number;
  // 실중량 정산 대상 항목 여부(주문 시점 스냅샷, PR-B).
  weightAdjustable: boolean;
  // 단위 기준 중량(g) 스냅샷. 실중량 환산 기준(옵션 중량은 별도로 더해진다).
  baseGramsSnapshot: number | null;
  // 준비완료 때 입력된 실제 중량(g). 기본+추가옵션을 합친 그 줄의 실제 총 중량. 미정이면 null(예상 상태).
  actualWeightG: number | null;
}

// 응답: 배달 정보 스냅샷. pickup 주문이면 null.
export interface OrderDeliveryDto {
  recipientName: string;
  recipientPhone: string;
  addressLine1: string;
  addressLine2: string | null;
  postalCode: string | null;
}

// 응답: 주문 전체 (사용자/관리자 공통 본문).
export interface OrderDto {
  id: string;
  orderNo: string;
  type: OrderType;
  status: OrderStatus;
  desiredDate: string | null;
  desiredTime: string | null;
  subtotalKrw: number;
  deliveryFeeKrw: number;
  discountKrw: number;
  pointsUsedKrw: number;
  // 칭찬하고 할인받기(개선 5~12)로 적용된 칭찬 문구/할인액 스냅샷. 미적용이면 null/0.
  complimentText: string | null;
  complimentDiscountKrw: number;
  totalKrw: number;
  // 실중량 정산(PR-B). 예상 상품합계/예상 최종금액(불변)과 실중량 확정 시각.
  //  - weighedAt 이 null 이면 아직 예상 상태(subtotal/total 은 예상치와 동일).
  estSubtotalKrw: number;
  estTotalKrw: number;
  weighedAt: string | null;
  // 적용된 쿠폰 요약 (없으면 빈 배열). 사용자/관리자 화면 표시용.
  appliedCoupons: { id: string; name: string; type: CouponType }[];
  delivery: OrderDeliveryDto | null;
  customerMemo: string | null;
  rejectReason: string | null;
  canceledBy: OrderCanceledBy | null;
  // 결제(인앱 PG). 현장/SMS 는 수동 처리, 인앱은 준비완료 시 자동 청구된다.
  //  - paymentStatus: unpaid -> charging -> paid | failed -> refunded | refund_failed.
  paymentMethodType: PaymentMethodType;
  paymentStatus: PaymentStatus;
  // 마지막 결제 실패 사유(짧은 코드). 실패 이력이 없으면 null. 관리자 표시용.
  paymentError: string | null;
  items: OrderItemDto[];
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

// 관리자 응답: 주문 + 주문자 식별 정보.
// 손님 PII(이름/휴대폰)는 관리자 콘솔에서만 노출되며, 사용자 응답(OrderDto)에는 포함하지 않는다.
export interface AdminOrderDto extends OrderDto {
  customer: {
    userId: string;
    name: string | null;
    phone: string | null;
  };
}

// 관리자 주문 목록 필터.
// type/status 는 선택. 미지정이면 전체.
export const adminOrderListQuerySchema = z.object({
  type: z.union([z.literal(ORDER_TYPE.PICKUP), z.literal(ORDER_TYPE.DELIVERY)]).optional(),
  status: z
    .union([
      z.literal(ORDER_STATUS.RECEIVED),
      z.literal(ORDER_STATUS.ACCEPTED),
      z.literal(ORDER_STATUS.REJECTED),
      z.literal(ORDER_STATUS.PREPARING),
      z.literal(ORDER_STATUS.READY),
      z.literal(ORDER_STATUS.COMPLETED),
      z.literal(ORDER_STATUS.CANCELED),
    ])
    .optional(),
});
export type AdminOrderListQuery = z.infer<typeof adminOrderListQuerySchema>;

// 희망 수령 시간을 손님 화면 표시 문자열로 변환한다.
//  - 날짜/시간이 모두 있으면 "MM/DD HH:MM" 형태.
//  - 비어 있으면 "가능한 빨리" (ASAP).
// 연도는 생략해 짧게 둔다 (예약 최대 60일 범위라 연도 혼동 위험 낮음).
export function formatDesiredPickup(desiredDate: string | null, desiredTime: string | null): string {
  if (!desiredDate || !desiredTime) {
    return "가능한 빨리";
  }
  // 'YYYY-MM-DD' -> 'MM/DD'
  const [, mm, dd] = desiredDate.split("-");
  return `${mm}/${dd} ${desiredTime}`;
}
